import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  fetchJob,
  fetchJobInvitations,
  fetchJobStatus,
  closeJobAction,
  markCheckpoint,
  clearCheckpoint,
  checkIfFirstView,
  fetchJobConnections
} from '../actions/job';
import {
  getJobById,
  getJobInvitationsById,
  getJobConnectionsById,
  getJobUIState,
  hasSeenCheckpoint,
  isJobLoaded
} from '../selectors/job';
import { getCategory } from '../selectors/category';
import { getLocation } from '../selectors/location';
import { Container, Row, Col, Card } from '../../legacy/hui/src/styled';
import { ModerationCard } from '../components/SuccessPageCards/ModerationCard';
import InvitingNotification from '../components/SuccessPageCards/InvitingNotification';
import { RespondedCard } from '../components/SuccessPageCards/RespondedCard';
import JobDetails from '../components/JobDetails';
import { getMapCenteredOnSuburb } from '../utils/maps';
import Poller from '../components/Poller';
import styled from 'styled-components';
import { SuccessPageNav } from '../components/SuccessPageNav';
import Checkpoints from '../constants/Checkpoints';
import CardTransition from '../components/CardTransition';
import JobStates from '../constants/JobStates';
import Helmet from 'react-helmet';
import { ContactSupportCard } from '../components/SuccessPageCards/ContactSupportCard';
import JobContextManager from '../components/JobContextManager';
import Loader from '../components/Loader';
import TradieCard from '../components/SuccessPageCards/TradieCard';
import { TitleBar } from '../components/TitleBar';
import { FeedbackCard } from '../components/SuccessPageCards/FeedbackCard';
import { submitFeedback } from '../actions/feedback';
import { setInstallToken } from '../actions/install';

const ConnectionPoller = Poller(({ duration, fetchJobConnections, job }, schedule) => {
  fetchJobConnections(job.id);
  schedule(duration);
})(() => false);

export const isWaitlisted = c => c.status === 5;
const not = f => i => !f(i);

//todo what height should map thing be?
//todo fix jerky animation when cards are removed (prob margin stuff)
//todo why do tradie cards, login card etc sometimes appear then hide???
const MapBg = styled(Card)`
  background-color: #d6d6d6;
  background-image: url(${props => getMapCenteredOnSuburb(props.suburb, props.postcode)});
  background-position: center;
  background-size: cover;
  padding: 10px;
  border-radius: 0;
  min-height: calc(100vh - 118px);
  flex-direction: column;
  display: flex;
  flex-grow: 1;
  align-items: center;
  font-size: 90%;
  margin-top: 0;
  @media (min-width: ${props => props.theme.breakpoints.sm}rem) {
    font-size: initial;
    padding: 20px;
  }

  @media (min-width: ${props => props.theme.breakpoints.md}rem) {
    font-size: initial;
    padding: 20px;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}rem) {
    border-radius: 10px;
    min-height: calc(100vh - 150px);
  }
`;

const NotFoundGate = ({ jobIsLoaded, children }) => {
  return jobIsLoaded ? <React.Fragment>{children}</React.Fragment> : <Loader />;
};

NotFoundGate.propTypes = {
  jobIsLoaded: PropTypes.bool,
  children: PropTypes.node
};

const Success = ({
  job,
  category = {},
  location = {},
  invitations = {},
  connections = [],
  fetchJobInvitations,
  markCheckpoint,
  showResponsedNotification,
  connectionCheckpoint,
  fetchJobConnections,
  jobState,
  jobIsLoaded,
  submitFeedback,
  feedback
}) => {
  const firstConnection = connections[0];

  return (
    <React.Fragment>
      <TitleBar pageTitle={'Installation Service'} />
      <NotFoundGate jobIsLoaded={jobIsLoaded}>
        <Helmet>
          <title>{`hipages - ${category ? category.person : ''} ${
            location.suburb ? `in ${location.suburb}` : ''
          }`}</title>
        </Helmet>
        <JobContextManager key={job.id} job={job} connections={connections} />
        <Container p={0} m={{ xs: 0, lg: 'auto' }} style={{ minWidth: 320 }}>
          <SuccessPageNav tradie={category.person} location={location.suburb} />
          <Row gutter={0}>
            <Col xs={12} lg={8} gutter={{ xs: 0, lg: 1 }} p={{ xs: 0, lg: 1 }}>
              <MapBg
                suburb={location.suburb}
                postcode={location.postcode}
                mt={{ xs: 0, lg: 1 }}
                mb={1}
              >
                <CardTransition
                  style={{
                    maxWidth: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1
                  }}
                >
                  {JobStates.MODERATION === jobState && (
                    <ModerationCard
                      key={'moderation'}
                      categoryPerson={category.person}
                      startAnimation={true}
                      onAnimationFinished={() =>
                        markCheckpoint(job.id, Checkpoints.MODERATION_FINISHED)
                      }
                    />
                  )}
                  {[JobStates.INVITE_ANIMATION, JobStates.WAITING_FOR_CONNECTION].includes(
                    jobState
                  ) && (
                    <InvitingNotification
                      key={'invitingNotfication'}
                      categoryPerson={category.person}
                      personPlural={category.personPlural}
                      thumbnails={invitations.thumbnails}
                      invitedCount={invitations.invitedCount}
                      hasSeenAnimation={JobStates.WAITING_FOR_CONNECTION === jobState}
                      onAnimationStarted={() =>
                        markCheckpoint(job.id, Checkpoints.INVITE_ANIMATION_FINISHED)
                      }
                      fetchJobInvitations={() => fetchJobInvitations(job.id)}
                    />
                  )}
                  {JobStates.WAITING_FOR_CONNECTION === jobState && (
                    <ConnectionPoller
                      key="connection-poller"
                      duration={60000}
                      fetchJobConnections={fetchJobConnections}
                      job={job}
                    />
                  )}
                  {JobStates.CONNECTED === jobState && showResponsedNotification && (
                    <RespondedCard
                      key={'respondedCard'}
                      respondedCount={Math.min(connections.length, 3)}
                      totalTradiesToNotify={3}
                      categoryPerson={category.person}
                      onClose={() => markCheckpoint(job.id, connectionCheckpoint)}
                    />
                  )}
                  {[JobStates.CONNECTED, JobStates.CONNECTED_CLOSED].includes(jobState) && (
                    <React.Fragment key={'connected'}>
                      {connections.filter(not(isWaitlisted)).map(c => (
                        <TradieCard key={c.id} connection={c} />
                      ))}
                    </React.Fragment>
                  )}

                  {JobStates.CLOSED === jobState && (
                    <ContactSupportCard key="ContactSupport" type="closed" messageLink={''} />
                  )}
                  {firstConnection && firstConnection.completedByTradie && (
                    <FeedbackCard
                      completedByConsumer={firstConnection.completedByConsumer}
                      jobAssignmentId={firstConnection.id}
                      key="feedback"
                      tradieType="tradie"
                      feedback={feedback}
                      submitFeedback={submitFeedback}
                    />
                  )}
                </CardTransition>
              </MapBg>
            </Col>
            <Col xs={12} lg={4}>
              <JobDetails job={job} category={category} location={location} />
            </Col>
          </Row>
        </Container>
      </NotFoundGate>
    </React.Fragment>
  );
};

Success.needs = (props, dispatch = {}, query = {}) => {
  const token = query.token || '';
  dispatch(setInstallToken(token));
  const job = dispatch(fetchJob(props.params.job));
  const invitations = dispatch(fetchJobInvitations(props.params.job));
  const connections = dispatch(fetchJobConnections(props.params.job));
  dispatch(checkIfFirstView(props.params.job));
  return Promise.all([job, invitations, connections]);
};

Success.propTypes = {
  job: PropTypes.object,
  category: PropTypes.object,
  location: PropTypes.object,
  fetchJobInvitations: PropTypes.func,
  invitations: PropTypes.object,
  markCheckpoint: PropTypes.func,
  jobState: PropTypes.oneOf(Object.values(JobStates)),
  connections: PropTypes.array,
  showResponsedNotification: PropTypes.bool,
  connectionCheckpoint: PropTypes.oneOf(Object.values(Checkpoints)),
  fetchJobConnections: PropTypes.func,
  jobIsLoaded: PropTypes.bool,
  submitFeedback: PropTypes.func,
  feedback: PropTypes.object
};

export const PollForStatus = Poller(({ fetchJobStatus, job }, schedule) => {
  if (!job.id) return;
  fetchJobStatus(job.id);
  const duration = 25000; // TODO change based on status
  schedule(duration);
})(Success);

const ConnectionCountToCheckpoint = {
  1: Checkpoints.DISMISSED_RESPONDED_NOTIFICATION_ONE,
  2: Checkpoints.DISMISSED_RESPONDED_NOTIFICATION_TWO,
  3: Checkpoints.DISMISSED_RESPONDED_NOTIFICATION_THREE
};

export default connect(
  (state, props) => {
    const { job: id } = props.match.params;
    const jobIsLoaded = isJobLoaded(state, id);
    const job = getJobById(state, id);
    const invitations = getJobInvitationsById(state, id);
    const category = job && getCategory(state, job.categoryId);
    const location = job && getLocation(state, job.suburbId);
    const jobState = job && getJobUIState(state, id);
    const connections = (job && getJobConnectionsById(state, id)) || [];
    const connectionCount = connections.length || 0;
    const connectionCheckpoint = ConnectionCountToCheckpoint[connectionCount] || null;
    const showResponsedNotification = Boolean(
      job &&
        connectionCount &&
        connectionCount <= 1 &&
        connectionCheckpoint &&
        !hasSeenCheckpoint(state, id, connectionCheckpoint)
    );
    const hasDismissedHiredNotification =
      job && hasSeenCheckpoint(state, id, Checkpoints.DISMISSED_HIRED_NOTIFICATION);

    const feedback = state.feedback;
    return {
      jobIsLoaded,
      job,
      invitations,
      category,
      location,
      jobState,
      showResponsedNotification,
      hasDismissedHiredNotification,
      connections,
      connectionCheckpoint,
      feedback
    };
  },
  {
    closeJobAction,
    fetchJobInvitations,
    fetchJobConnections,
    fetchJobStatus,
    markCheckpoint,
    clearCheckpoint,
    checkIfFirstView,
    submitFeedback
  }
)(PollForStatus);
